<template>
  <div class="tool-detail-page">
    <!-- Page Content Container -->
    <div class="page-content">
      <!-- Page Header Area -->
      <div class="page-header">
        <div class="header-content">
          <div class="left-section">
            <v-btn
              icon
              size="medium"
              variant="text"
              @click="goBack"
              class="back-btn">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              {{ toolName }}
            </h1>
          </div>

          <div class="right-section">
            <v-btn
              v-if="toolId"
              icon
              size="small"
              variant="text"
              color="primary"
              class="call-btn mank-btn-icon mr-4 mank-btn-icon-md"
              title="Delete tool"
              @click="openDeleteDialog">
              <Icon name="trash" variant="solid" color="default" size="16"/>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="content-section">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"/>
          <p class="loading-text">
            Loading tool details...
          </p>
        </div>

        <!-- Main Content -->
        <div v-else class="main-content">
          <!-- Tool Info Table -->
          <div class="tool-info-table">
            <div class="table-rows">
              <div class="table-row">
                <span class="row-label">Tool ID</span>
                <div class="row-value-with-copy">
                  <span class="row-value">{{ toolId }}</span>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="copyToolId"
                    class="copy-btn">
                    <Icon name="copy" variant="solid" size="16" color="default"/>
                  </v-btn>
                </div>
              </div>

              <div class="table-row">
                <span class="row-label">Date Created</span>
                <span class="row-value">{{ formatDate(dateCreated) }}</span>
              </div>

              <div class="table-row">
                <span class="row-label">Description</span>
                <span class="row-value description">{{ description }}</span>
              </div>
            </div>
          </div>

          <!-- Tabs Area -->
          <div class="tabs-area">
            <div class="tabs-container">
              <v-btn
                :class="['tab-btn', { active: activeTab === 'details' }]"
                @click="setActiveTab('details')"
                variant="text">
                Details
              </v-btn>
              <v-btn
                :class="['tab-btn', { active: activeTab === 'test' }]"
                @click="setActiveTab('test')"
                variant="text">
                Test
              </v-btn>
              <v-btn
                :class="['tab-btn', { active: activeTab === 'history' }]"
                @click="setActiveTab('history')"
                variant="text">
                History
              </v-btn>
            </div>
          </div>

          <!-- Content Area Based on Active Tab -->
          <!-- Details Tab -->
          <div v-if="activeTab === 'details'" class="tab-content details-content">
            <div class="accordions-area">
              <div
                v-for="accordion in accordions"
                :key="accordion.id"
                class="accordion">
                <!-- Title Area -->
                <div class="title-area">
                  <div class="title-section">
                    <h3 class="accordion-title">
                      {{ accordion.title }}
                    </h3>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="toggleAccordion(accordion.id)"
                      class="mank-btn-icon expand-btn">
                      <Icon
                        name="expand-up"
                        variant="solid"
                        size="16"
                        color="default"
                        :class="{ 'rotated': accordion.expanded }"/>
                    </v-btn>
                  </div>
                </div>

                <!-- Accordion Content -->
                <div v-if="accordion.expanded" class="accordion-content">
                  <div class="content-table">
                    <div
                      v-for="(item, index) in accordion.content"
                      :key="index"
                      class="table-row"
                      :class="{ 'has-divider': item.divider }">
                      <div class="row-content">
                        <span class="row-label">{{ item.label }}</span>
                        <div class="row-value">
                          <v-icon
                            v-if="item.icon"
                            :color="item.iconColor"
                            size="16"
                            class="status-icon">
                            {{ item.icon }}
                          </v-icon>
                          <span
                            class="value-text"
                            :class="{ 'link': item.isLink }">
                            {{ item.value }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- History Tab Content -->
          <div v-if="activeTab === 'history'" class="tab-content history-content">
            <!-- Call History Table -->
            <div class="call-history-table">
              <!-- Table Header -->
              <div class="table-header">
                <div class="header-item call-id">
                  Call ID
                </div>
                <div class="header-item status">
                  Status
                </div>
                <div class="header-item start-time">
                  Start Time
                  <v-icon size="16" class="sort-icon">
                    mdi-sort
                  </v-icon>
                </div>
                <div class="header-item end-time">
                  End Time
                  <v-icon size="16" class="sort-icon">
                    mdi-sort
                  </v-icon>
                </div>
                <div class="header-item duration">
                  Duration
                  <v-icon size="16" class="sort-icon">
                    mdi-sort
                  </v-icon>
                </div>
                <div class="header-item tool-errors">
                  Tool Errors
                  <v-icon size="16" class="sort-icon">
                    mdi-sort
                  </v-icon>
                </div>
                <div class="header-item summary">
                  Summary
                </div>
              </div>

              <!-- Table Rows -->
              <div class="table-rows">
                <div
                  v-for="call in paginatedCallHistory"
                  :key="call.id"
                  class="table-row">
                  <div class="row-item call-id">
                    {{ call.callId }}
                  </div>
                  <div class="row-item status">
                    <v-chip
                      :class="['status-chip', call.status.toLowerCase()]"
                      size="small"
                      variant="outlined">
                      {{ call.status }}
                    </v-chip>
                  </div>
                  <div class="row-item start-time">
                    {{ formatDateTime(call.startTime) }}
                  </div>
                  <div class="row-item end-time">
                    {{ call.endTime ? formatDateTime(call.endTime) : '-' }}
                  </div>
                  <div class="row-item duration">
                    {{ call.duration || '-' }}
                  </div>
                  <div class="row-item tool-errors">
                    {{ call.toolErrors || '-' }}
                  </div>
                  <div class="row-item summary">
                    {{ call.summary || '-' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Table Pagination -->
            <div class="table-pagination">
              <div class="pagination-left">
                <div class="pagination-arrows">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    :disabled="currentPage === 1"
                    @click="previousPage"
                    class="mank-btn-icon">
                    <Icon name="arrow-left" variant="solid" size="16" color="default"/>
                  </v-btn>

                  <div class="page-numbers">
                    <span
                      v-for="page in visiblePages"
                      :key="page"
                      :class="['page-number', { active: page === currentPage }]"
                      @click="goToPage(page)">
                      {{ page }}
                    </span>
                  </div>

                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    :disabled="currentPage === totalPages"
                    @click="nextPage"
                    class="mank-btn-icon">
                    <Icon name="arrow-right" variant="solid" size="16" color="default"/>
                  </v-btn>
                </div>
              </div>

              <div class="pagination-right">
                <span class="showing-text">Showing</span>
                <v-select
                  v-model="itemsPerPage"
                  :items="[10, 25, 50]"
                  @update:modelValue="updatePagination"
                  class="items-per-page-select"
                  hide-details
                  outlined
                  dense>
                  <template v-slot:append>
                    <Icon name="expand-down" variant="solid" size="16" color="muted"/>
                  </template>
                </v-select>
                <span class="total-text">/ {{ totalItems }}</span>
              </div>
            </div>
          </div>

          <!-- Test Tab Content -->
          <div v-if="activeTab === 'test'" class="tab-content test-content">
            <div class="test-cards">
              <!-- Input Card -->
              <div class="test-card">
                <div class="card-header">
                  <div class="card-title">
                    <h3 class="title-text">
                      Input
                    </h3>
                    <v-btn
                      variant="flat"
                      color="primary"
                      size="small"
                      @click="testInput"
                      class="mank-btn">
                      Test Input
                    </v-btn>
                  </div>
                </div>

                <div class="card-content">
                  <div class="code-area input-area">
                    <MankTextarea
                      v-model="testInputData"
                      placeholder="Enter test input data..."
                      rows="15"/>
                  </div>
                </div>
              </div>

              <!-- Output Card -->
              <div class="test-card">
                <div class="card-header">
                  <div class="card-title">
                    <h3 class="title-text">
                      Output
                    </h3>
                    <v-btn
                      icon
                      size="small"
                      variant="flat"
                      color="primary"
                      :disabled="!testOutputData"
                      @click="copyOutput"
                      class="mank-btn-icon">
                      <Icon name="copy" variant="solid" size="16" color="onPrimary"/>
                    </v-btn>
                  </div>
                </div>

                <div class="card-content">
                  <div class="code-area output-area">
                    <pre v-if="testOutputData" class="output-pre">{{ testOutputData }}</pre>
                    <div v-else class="empty-output">
                      <p>No output yet. Click "Test Input" to generate output.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import ToolService from '../services/ToolService';
import Icon from '../components/Icon.vue';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';

export default {
  name: 'ToolDetail',
  components: { Icon, DeleteConfirmationModal },
  props: { id: String },
  data() {
    return {
      loading: false,
      tool: null,
      activeTab: 'details',
      currentPage: 1,
      itemsPerPage: 10,
      callHistory: [],
      testInputData: '',
      testOutputData: null,
      showDeleteDialog: false,
    };
  },
  computed: {
    toolName() {
      return this.tool?.name || 'Loading...';
    },
    toolId() {
      return this.tool?.id || '';
    },
    dateCreated() {
      return this.tool?.created_at || '';
    },
    description() {
      return this.tool?.description || '';
    },
    accordions() {
      if (!this.tool) return [];

      const accordions = [];

      accordions.push({
        id: 'http-info',
        title: 'HTTP Tool Information',
        expanded: true,
        content: [
          {
            label: 'Server URL',
            value: this.tool.endpoint_url || '',
            isLink: true,
          },
          {
            label: 'Method',
            value: this.tool.method || '',
          },
          {
            label: 'Timeout',
            value: this.tool.timeout ? `${this.tool.timeout}s` : 'Not set',
          },
        ],
      });

      if (this.tool.pre_computable !== undefined) {
        accordions.push({
          id: 'precomputable',
          title: 'Precomputable',
          expanded: true,
          content: [
            {
              label: 'Status',
              value: this.tool.pre_computable ? 'Yes' : 'No',
              icon: this.tool.pre_computable ? 'mdi-check-circle' : 'mdi-close-circle',
              iconColor: this.tool.pre_computable ? '#00AC4F' : '#E60004',
            },
          ],
        });
      }

      if (this.tool.parameters && Object.keys(this.tool.parameters).length > 0) {
        const paramContent = [];
        Object.entries(this.tool.parameters).forEach(([key, value]) => {
          paramContent.push({
            label: 'Name',
            value: key,
          });
          paramContent.push({
            label: 'Type',
            value: typeof value === 'object' ? JSON.stringify(value) : String(value),
            divider: true,
          });
        });

        accordions.push({
          id: 'parameters',
          title: 'Parameters',
          expanded: true,
          content: paramContent,
        });
      }

      if (this.tool.headers && Object.keys(this.tool.headers).length > 0) {
        const headerContent = [];
        Object.entries(this.tool.headers).forEach(([key, value]) => {
          headerContent.push({
            label: key,
            value: String(value),
          });
        });

        accordions.push({
          id: 'headers',
          title: 'Headers',
          expanded: true,
          content: headerContent,
        });
      }

      return accordions;
    },
    totalItems() {
      return this.callHistory.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    paginatedCallHistory() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.callHistory.slice(start, end);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= maxVisible) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else if (this.currentPage <= 3) {
        pages.push(1, 2, 3, '...', this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1, '...', this.totalPages - 2, this.totalPages - 1, this.totalPages);
      } else {
        pages.push(1, '...', this.currentPage, '...', this.totalPages);
      }

      return pages;
    },
  },
  async mounted() {
    await this.fetchTool();
  },
  methods: {
    async fetchTool() {
      this.loading = true;
      try {
        const toolId = this.id || this.$route.params.id;
        const response = await ToolService.getToolById(toolId);
        this.tool = response.data || response;
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load tool details',
        });
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    openDeleteDialog() {
      if (!this.toolId) return;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
    },
    async confirmDelete() {
      if (!this.toolId) {
        this.showDeleteDialog = false;
        return;
      }
      try {
        await ToolService.deleteTool(this.toolId);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Tool deleted successfully',
        });
        this.$router.push({ name: 'Tools' });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to delete tool',
        });
      } finally {
        this.showDeleteDialog = false;
      }
    },
    async copyToolId() {
      if (!this.toolId) return;

      try {
        await navigator.clipboard.writeText(this.toolId);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Tool ID copied to clipboard',
        });
      } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = this.toolId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Tool ID copied to clipboard',
        });
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    toggleAccordion(accordionId) {
      const accordion = this.accordions.find((acc) => acc.id === accordionId);
      if (accordion) {
        accordion.expanded = !accordion.expanded;
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    updatePagination() {
      this.currentPage = 1;
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).replace(',', ',');
    },
    testInput() {
      if (!this.testInputData.trim()) {
        this.$store.dispatch('ui/showNotification', {
          type: 'warning',
          title: 'Warning',
          message: 'Please enter test input data',
        });
        return;
      }

      try {
        const inputData = JSON.parse(this.testInputData);

        const mockOutput = {
          status: 'success',
          result: {
            audioGenerated: true,
            duration: '2.4s',
            format: 'wav',
            sampleRate: 44100,
            digits: inputData.digits || '',
            timestamp: new Date().toISOString(),
          },
          metadata: {
            processingTime: '125ms',
            toolVersion: '1.0.0',
            executionId: 'exec_' + Math.random().toString(36).substr(2, 9),
          },
        };

        this.testOutputData = JSON.stringify(mockOutput, null, 2);
      } catch (error) {
        const errorOutput = {
          status: 'error',
          error: {
            code: 'INVALID_INPUT',
            message: 'Invalid JSON format in input data',
            details: error.message,
          },
          timestamp: new Date().toISOString(),
        };

        this.testOutputData = JSON.stringify(errorOutput, null, 2);
      }
    },
    async copyOutput() {
      if (!this.testOutputData) return;

      try {
        await navigator.clipboard.writeText(this.testOutputData);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Output copied to clipboard',
        });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to copy output',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
  padding: 48px 24px;

  .loading-text {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 400;
    font-size: $body-3-size;
    color: $light-foreground-2;
    margin: 0;
  }
}

.content-section {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
}

.main-content {
  align-self: stretch;
  gap: 10px;
}

.tool-info-table {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  align-self: stretch;
  gap: 24px;
  padding: 24px;
  background-color: $light-surface-2;

  .table-rows {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    .table-row {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-self: stretch;
      gap: 4px;

      .row-label {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 400;
        font-size: $body-3-size;
        line-height: 1.2;
        color: $light-foreground-4;
      }

      .row-value-with-copy {
        display: flex;
        align-items: center;
        align-self: stretch;
        gap: 8px;

        .row-value {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: $body-3-size;
          line-height: 1.2;
          color: $light-foreground-1;
        }

        .copy-btn {
          width: 24px;
          height: 24px;
          padding: 4px;
          background-color: $light-surface-1;

          :deep(.v-icon) {
            color: $light-foreground-1;
          }
        }
      }

      .row-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $body-3-size;
        line-height: 1.2;
        color: $light-foreground-1; // #171D22 - Figma'daki value color

        &.description {
          line-height: 1.2; // Figma'da description da aynı line-height
        }
      }
    }
  }
}

.tabs-area {
  border-bottom: 1px solid $light-border; // #D3DAE0 - Figma'daki tabs border
  padding: 12px 24px 0px;
  gap: 10px;

  .tabs-container {
    display: flex;
    align-self: stretch;

    .tab-btn {
      height: 32px;
      padding: 8px 12px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600;
      font-size: $body-3-size;
      line-height: 1.2;
      text-transform: none;
      letter-spacing: normal;
      color: $light-foreground-4; // #93A0AF - Default tab color
      border-radius: 0;
      border-bottom: 2px solid transparent;

      &.active {
        color: $light-foreground-1; // #171D22 - Active tab color
        border-bottom-color: $light-primary; // #EB575D - Active tab border
      }

      &:hover {
        background-color: transparent;
      }
    }
  }
}

.accordions-area {
  padding: 24px;
  gap: 16px;

  .accordion {
    border: 1px solid $light-border; // #D3DAE0
    border-radius: 12px;
    margin-bottom: 16px;
    background-color: $light-surface-2;
    overflow: hidden;

    .title-area {
      padding: 8px 12px;
      gap: 10px;
      background-color: $light-surface-1;
      border-bottom: 1px solid $light-border;

      .title-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        gap: 157px;

        .accordion-title {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 700;
          font-size: $body-2-size;
          line-height: 1.4;
          color: $light-foreground-1;
          margin: 0;
        }

        .expand-btn {
          width: 24px;
          height: 24px;
          padding: 4px;
          background-color: transparent;

          :deep(.v-icon) {
            color: $light-foreground-1;
            transition: transform 0.2s ease;

            &.rotated {
              transform: rotate(180deg);
            }
          }
        }
      }
    }

    .accordion-content {
      background-color: $light-surface-2;

      .content-table {
        padding: 12px;
        gap: 24px;

        .table-row {
          padding: 12px 0;

          &.has-divider {
            border-top: 1px solid $light-border; // #D3DAE0
            margin-top: 12px;
            padding-top: 24px;
          }

          .row-content {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .row-label {
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-weight: 400;
              font-size: $body-3-size;
              line-height: 1.2;
              color: $light-foreground-4;
            }

            .row-value {
              display: flex;
              align-items: center;
              gap: 4px;

              .status-icon {
                margin-right: 4px;
              }

              .value-text {
                font-family: 'Plus Jakarta Sans', sans-serif;
                font-weight: 600;
                font-size: $body-3-size;
                line-height: 1.2;
                color: $light-foreground-1;

                &.link {
                  color: $light-info;
                  cursor: pointer;

                  &:hover {
                    text-decoration: underline;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

.history-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
}

.call-history-table {
  display: flex;
  flex-direction: column;
  background-color: $light-surface-2;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 200px 140px 200px 200px 120px 120px 1fr;
  background-color: $light-surface-1; // #F7F8FB
  border-radius: 8px 8px 0 0;

  .header-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $body-3-size;
    line-height: 1.2;
    color: $light-foreground-4; // #5C6A75
    height: 48px;

    &.call-id,
    &.summary {
      justify-content: flex-start;
    }

    .sort-icon {
      color: $light-foreground-4; // #5C6A75
    }
  }
}

.table-rows {
  display: flex;
  flex-direction: column;

  .table-row {
    display: grid;
    grid-template-columns: 200px 140px 200px 200px 120px 120px 1fr;
    align-items: center;
    border-bottom: 1px solid $light-surface-3; // #D3DAE0
    background-color: $light-surface-2;

    &:last-child {
      border-bottom: none;
    }

    .row-item {
      padding: 10px 12px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 400;
      font-size: $body-3-size;
      line-height: 1.2;
      color: $light-foreground-1; // #171D22
      height: 48px;
      display: flex;
      align-items: center;

      &.call-id,
      &.summary {
        justify-content: flex-start;
      }

      &.status {
        justify-content: center;
      }

      &.start-time,
      &.end-time,
      &.duration,
      &.tool-errors {
        justify-content: center;
      }

      .status-chip {
        height: 24px;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $caption-1-size;
        line-height: 1.2;
        padding: 4px 8px;
        border-radius: 200px;

        &.live {
          color: $light-success !important; // #00AC4F
          border-color: $light-success-neutral !important; // #66CD95
        }

        &.ended {
          color: $light-foreground-1 !important; // #171D22
          border-color: $light-foreground-4 !important; // #93A0AF
        }

        &.canceled,
        &.no.answer {
          color: $light-warning !important; // #EFB424
          border-color: $light-warning-neutral !important; // #F7DA92
        }

        &.connection.error {
          color: $light-error !important; // #E60004
          border-color: $light-error-neutral !important; // #FF7070
        }
      }
    }
  }
}

.table-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 155px;
  padding: 8px 12px;
  height: 48px;
  background-color: $light-surface-2;

  .pagination-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .pagination-arrows {
      display: flex;
      align-items: center;
      gap: 12px;

      .pagination-btn {
        width: 24px;
        height: 24px;
        background-color: transparent;
        border: none;

        &:disabled {
          .v-icon {
            color: $light-surface-4 !important; // #B7C0CA
          }
        }

        .v-icon {
          color: $light-foreground-4; // #93A0AF
        }
      }

      .page-numbers {
        display: flex;
        gap: 8px;

        .page-number {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: $caption-1-size;
          line-height: 1.2;
          text-align: center;
          color: $light-foreground-4; // #5C6A75
          border-radius: 4px;
          cursor: pointer;

          &.active {
            color: $light-primary; // #EB575D
            border: 1px solid $light-primary-neutral; // #F6A7A9
            background-color: transparent;
          }

          &:hover:not(.active) {
            background-color: $light-surface-1;
          }
        }
      }
    }
  }

  .pagination-right {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    .showing-text,
    .total-text {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600;
      font-size: $caption-1-size;
      line-height: 1.2;
      color: $light-foreground-4; // #93A0AF
    }

    .per-page-selector {
      position: relative;
      width: 48px;

      .per-page-select {
        width: 100%;
        padding: 6px 4px;
        padding-right: 20px;
        border: 1px solid $light-surface-3; // #D3DAE0
        border-radius: 4px;
        background-color: $light-surface-2;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $caption-1-size;
        line-height: 1.2;
        color: $light-foreground-4; // #93A0AF
        text-align: center;
        appearance: none;
        cursor: pointer;

        &:focus {
          outline: none;
          border-color: $light-primary; // #EB575D
        }
      }

      .dropdown-icon {
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        color: $light-foreground-4; // #93A0AF
        pointer-events: none;
      }
    }
  }
}

.test-content {
  padding: 24px;

  .test-cards {
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    align-self: stretch;
    gap: 24px;

    .test-card {
      display: flex;
      flex-direction: column;
      flex: 1;
      border: 1px solid $light-surface-3; // #D3DAE0
      border-radius: 12px;
      overflow: hidden;
      background-color: $light-surface-2;

      .card-header {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        gap: 10px;
        padding: 8px 12px;
        background-color: $light-surface-1;
        border-bottom: 1px solid $light-surface-3; // #D3DAE0

        .card-title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          gap: 157px;

          .title-text {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: $body-2-size;
            line-height: 1.4;
            color: $light-foreground-1;
            margin: 0;
          }

          .test-input-btn {
            height: 24px;
            padding: 4px 8px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: $caption-1-size;
            line-height: 1.2;
            background-color: $light-primary; // #EB575D
            color: $light-surface-2; // #FFFFFF

            &:hover {
              background-color: darken($light-primary, 5%);
            }
          }

          .copy-output-btn {
            width: 24px;
            height: 24px;
            padding: 4px;
            background-color: $light-primary-neutral; // #F6A7A9
            color: $light-surface-2; // #FFFFFF

            &:disabled {
              background-color: $light-primary-neutral; // #F6A7A9
              opacity: 0.5;
              cursor: not-allowed;
            }

            &:hover:not(:disabled) {
              background-color: $light-primary; // #EB575D
            }
          }
        }
      }

      .card-content {
        display: flex;
        justify-content: stretch;
        align-items: stretch;
        align-self: stretch;
        gap: 24px;
        padding: 12px;

        .code-area {
          flex: 1;
          height: 320px;
          border-radius: 8px;
          background-color: $light-foreground-1; // #171D22
          overflow: hidden;

          &.input-area {
            .code-textarea {
              width: 100%;
              height: 100%;
              padding: 12px;
              border: none;
              outline: none;
              background-color: transparent;
              color: $light-surface-2; // #FFFFFF
              font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
              font-size: $text-size-13;
              line-height: 1.4;
              resize: none;

              &::placeholder {
                color: $light-surface-4; // #B7C0CA
              }

              &:focus {
                outline: none;
              }
            }
          }

          &.output-area {
            display: flex;
            flex-direction: column;

            .output-pre {
              flex: 1;
              padding: 12px;
              margin: 0;
              background-color: transparent;
              color: $light-surface-2; // #FFFFFF
              font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
              font-size: $text-size-13;
              line-height: 1.4;
              overflow: auto;
              white-space: pre-wrap;
              word-wrap: break-word;
            }

            .empty-output {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;

              p {
                color: $light-surface-4; // #B7C0CA
                font-family: 'Plus Jakarta Sans', sans-serif;
                font-size: $body-3-size;
                text-align: center;
                margin: 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>
